import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  useToast,
} from '@chakra-ui/react';
import { useSearch } from '../../../contexts/SearchContext';
import { getData, postData } from '../../../utils/httpClient';
import DynamicTable from '../../../components/table/DynamicTable';
import { columnsDataDictatedCourses } from './variables/columnsDataDictatedCourses';
import CreateDictatedCourseModal from './CreateModal';
import ConfirmModal from '../../../components/modal/ConfirmModal';

function DictatedCoursesList() {
  const { searchQuery, updateSearchQuery } = useSearch();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalState, setModalState] = useState({
    isOpen: false,
    confirmModalOpen: false,
  });

  const [formData, setFormData] = useState({});

  const rowsPerPage = 10;
  const toast = useToast();

  const latestRequestRef = useRef(0);

  const getDictatedCourses = async (searchData) => {
    const requestId = ++latestRequestRef.current;
    try {
      const response = await getData(
        `/dictated-courses?page=${page + 1}&limit=${rowsPerPage}&data=${searchData}`,
      );
      if (requestId !== latestRequestRef.current) return;
      setData(response?.payload?.courses || []);
      setTotalPages(Math.ceil(response?.payload?.total / rowsPerPage));
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      toast({
        title: 'Error al obtener dictados',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const createDictatedCourse = async () => {
    try {
      const response = await postData('/dictated-courses', formData);
      if (response?.payload) {
        toast({
          title: 'Dictado creado con éxito',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        closeModal();
        getDictatedCourses('');
      }
    } catch (error) {
      toast({
        title: 'Error al crear dictado',
        description: error.response?.data?.message || 'Error interno del servidor',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getDictatedCourses(searchQuery);
  }, [page, searchQuery]);

  const resetForm = () => {
    setFormData({});
  };

  const openModal = () => {
    setModalState({
      isOpen: true,
      confirmModalOpen: false,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      confirmModalOpen: false,
    });
    resetForm();
  };

  const showConfirmModal = () => {
    setModalState({
      isOpen: true,
      confirmModalOpen: true,
    });
  };

  const hideConfirmModal = () => {
    setModalState({
      isOpen: true,
      confirmModalOpen: false,
    });
  };

  const handleConfirm = async () => {
    await createDictatedCourse();
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex justify="flex-end" mb={4}>
        <Button colorScheme="blue" onClick={openModal}>
          Nuevo Dictado
        </Button>
      </Flex>
      
      <SimpleGrid mb="20px" columns={{ sm: 1 }} spacing={{ base: '20px', xl: '20px' }}>
        <DynamicTable
          columnsData={columnsDataDictatedCourses}
          data={data}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          openModal={openModal}
          showRowInfo={false}
        />
      </SimpleGrid>

      <CreateDictatedCourseModal
        isOpen={modalState.isOpen && !modalState.confirmModalOpen}
        onClose={closeModal}
        formData={formData}
        setFormData={setFormData}
        onConfirm={showConfirmModal}
      />

      <ConfirmModal
        isOpen={modalState.confirmModalOpen}
        onClose={hideConfirmModal}
        onConfirm={handleConfirm}
        header="Confirmar creación"
        body="¿Está seguro que desea crear este dictado?"
      />
    </Box>
  );
}

export default DictatedCoursesList;
